import axios from 'axios';
import { User, Role, Session, AuthOptions, AuditLogPage, AuditFacets } from '../types';

const authApi = axios.create({
  baseURL: '/api/v2',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Auth
export const login = (data: { email: string; password: string; rememberMe?: boolean }) =>
  authApi.post<{ user: User }>('/auth/login', data).then(r => r.data);

export const register = (data: { email: string; password: string; name: string }) =>
  authApi.post<{ user: User }>('/auth/register', data).then(r => r.data);

export const logout = () =>
  authApi.post('/auth/logout').then(r => r.data);

export const refreshToken = () =>
  authApi.post<{ user: User }>('/auth/refresh').then(r => r.data);

export const getMe = () =>
  authApi.get<{ user: User }>('/auth/me').then(r => r.data);

export const forgotPassword = (email: string) =>
  authApi.post<{ message: string }>('/auth/forgot-password', { email }).then(r => r.data);

export const resetPassword = (data: { token: string; password: string }) =>
  authApi.post<{ message: string }>('/auth/reset-password', data).then(r => r.data);

// Users
export const getUsers = (params?: Record<string, string>) =>
  authApi.get<User[]>('/users', { params }).then(r => r.data);

export const getUser = (id: number) =>
  authApi.get<{ user: User }>(`/users/${id}`).then(r => r.data);

export const updateUser = (id: number, data: Partial<User>) =>
  authApi.put<{ user: User }>(`/users/${id}`, data).then(r => r.data);

export const changePassword = (id: number, data: { currentPassword: string; newPassword: string }) =>
  authApi.put<{ message: string }>(`/users/${id}/password`, data).then(r => r.data);

export const deleteUser = (id: number) =>
  authApi.delete(`/users/${id}`);

export const getUserSessions = (id: number) =>
  authApi.get<Session[]>(`/users/${id}/sessions`).then(r => r.data);

export const revokeSession = (userId: number, sessionId: string) =>
  authApi.delete(`/users/${userId}/sessions/${sessionId}`);

// Roles
export const getRoles = () =>
  authApi.get<Role[]>('/users/roles').then(r => r.data);

/** Public: what the sign-in screens need before anyone is authenticated. */
export const getAuthOptions = () =>
  authApi.get<AuthOptions>('/auth/options').then(r => r.data);

// Audit log (audit.view)
export interface AuditQuery {
  user_id?: number;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  from?: string;
  to?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export const getAuditLog = (params: AuditQuery) =>
  authApi.get<AuditLogPage>('/audit', { params }).then(r => r.data);

export const getAuditFacets = () =>
  authApi.get<AuditFacets>('/audit/facets').then(r => r.data);
